import { type UseQueryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import type { SpecConversationSummary, SpecWebClient } from './spec.js';

/**
 * Shared conversation history for the two surfaces that show it: the sidebar
 * "Chats" section and the recents list on the Spec home screen.
 *
 * A react-query key rather than runtime state on purpose. Both surfaces mount
 * independently (the sidebar is outside the Spec route), and neither owns the
 * list; the cache is what lets them read the same fetch and lets a rename,
 * delete, or newly created thread refresh both at once.
 */
export function specConversationsQueryKey(workspaceId: string | undefined) {
  return ['spec', 'conversations', workspaceId ?? null] as const;
}

/**
 * Search param naming the chat the Spec route should open; absent means "start
 * a new one".
 *
 * Which chat is open is URL state, not something the sidebar pushes into the
 * runtime before navigating. That ordering could not be made safe: the Spec
 * page is lazy, so a chat opened by the sidebar landed while the page chunk was
 * still downloading and the page's arrival handling then discarded it.
 *
 * It lives here, not in the route module, so the sidebar can name the param
 * without importing a route (and dragging a route definition into its chunk).
 */
export const SPEC_CHAT_SEARCH_KEY = 'c';
/** A question another site hands Spec, as `/spec?ask=`; it lands in the composer, never sends. */
export const SPEC_ASK_SEARCH_KEY = 'ask';
const SPEC_ASK_MAX = 2000;

export function readSpecAsk(search: unknown): string | null {
  if (typeof search !== 'object' || search === null) return null;
  const value = (search as Record<string, unknown>)[SPEC_ASK_SEARCH_KEY];
  if (typeof value !== 'string') return null;
  const text = value.trim().slice(0, SPEC_ASK_MAX);
  return text.length > 0 ? text : null;
}

export function readSpecChatId(search: unknown): string | null {
  if (typeof search !== 'object' || search === null) return null;
  const value = (search as Record<string, unknown>)[SPEC_CHAT_SEARCH_KEY];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function useSpecConversations({
  workspaceId,
  client,
  enabled = true,
  refetchInterval = false,
}: {
  workspaceId: string | undefined;
  client: SpecWebClient;
  enabled?: boolean;
  /** Poll while a run is in flight on a chat nobody is watching; it settles server-side. */
  refetchInterval?: UseQueryOptions<readonly SpecConversationSummary[]>['refetchInterval'];
}) {
  const queryFn = useSpecConversationsQueryFn(workspaceId, client);
  return useQuery<readonly SpecConversationSummary[]>({
    queryKey: specConversationsQueryKey(workspaceId),
    enabled: Boolean(workspaceId) && enabled,
    refetchInterval,
    queryFn,
  });
}

/** One chat's seen time from the cached list, never fetching: undefined when unlisted, null from an older API. */
export function useCachedSpecChatSeenAt(
  workspaceId: string | undefined,
  client: SpecWebClient,
  conversationId: string | null,
): string | null | undefined {
  const queryFn = useSpecConversationsQueryFn(workspaceId, client);
  const select = React.useCallback(
    (list: readonly SpecConversationSummary[]) => {
      const entry = list.find((conversation) => conversation.id === conversationId);
      return entry ? (entry.seenAnswerAt ?? null) : undefined;
    },
    [conversationId],
  );
  return useQuery<readonly SpecConversationSummary[], Error, string | null | undefined>({
    queryKey: specConversationsQueryKey(workspaceId),
    enabled: false,
    queryFn,
    select,
  }).data;
}

// Seen only moves forward on the server, so a list read that began before a mark landed keeps the later cached time.
function useSpecConversationsQueryFn(workspaceId: string | undefined, client: SpecWebClient) {
  const queryClient = useQueryClient();
  return React.useCallback(async () => {
    const list = await client.listConversations(workspaceId as string);
    const cached = queryClient.getQueryData<readonly SpecConversationSummary[]>(
      specConversationsQueryKey(workspaceId),
    );
    if (!cached) return list;
    const seen = new Map(cached.map((entry) => [entry.id, entry.seenAnswerAt]));
    return list.map((entry) => {
      const kept = seen.get(entry.id);
      return kept && entry.seenAnswerAt && Date.parse(kept) > Date.parse(entry.seenAnswerAt)
        ? { ...entry, seenAnswerAt: kept }
        : entry;
    });
  }, [client, queryClient, workspaceId]);
}

/** Invalidate both readers after a write, or after a send creates a thread. */
export function useRefreshSpecConversations(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return React.useCallback(
    () => queryClient.invalidateQueries({ queryKey: specConversationsQueryKey(workspaceId) }),
    [queryClient, workspaceId],
  );
}

/** A finished reply newer than what the member has seen; a list without the field reads as seen. */
export function isSpecChatUnread(
  conversation: Pick<SpecConversationSummary, 'lastAnswerAt' | 'seenAnswerAt'>,
): boolean {
  const { lastAnswerAt, seenAnswerAt } = conversation;
  if (!lastAnswerAt || !seenAnswerAt) return false;
  return Date.parse(lastAnswerAt) > Date.parse(seenAnswerAt);
}

/** Marks a chat seen on the server and in the cached list at once, so its dot clears without a refetch. */
export function useMarkSpecConversationSeen(
  workspaceId: string | undefined,
  client: SpecWebClient | undefined,
) {
  const queryClient = useQueryClient();
  return React.useCallback(
    async (
      conversation: Pick<SpecConversationSummary, 'id' | 'lastAnswerAt'>,
    ): Promise<boolean> => {
      if (!workspaceId || !client) return false;
      const queryKey = specConversationsQueryKey(workspaceId);
      const update = (next: (seen: string | null | undefined) => string | null | undefined) =>
        queryClient.setQueryData<readonly SpecConversationSummary[]>(queryKey, (list) =>
          list?.map((entry) =>
            entry.id === conversation.id
              ? { ...entry, seenAnswerAt: next(entry.seenAnswerAt) }
              : entry,
          ),
        );
      const before = queryClient
        .getQueryData<readonly SpecConversationSummary[]>(queryKey)
        ?.find((entry) => entry.id === conversation.id)?.seenAnswerAt;
      const optimistic = conversation.lastAnswerAt;
      if (optimistic) update((seen) => laterOf(seen, optimistic));
      try {
        const marked = await client.markConversationSeen(workspaceId, conversation.id);
        update((seen) => laterOf(seen, marked.seenAnswerAt));
        return true;
      } catch {
        // The server kept its old time, so the dot comes back rather than hiding an unsaved mark.
        if (optimistic) update((seen) => (seen === optimistic ? before : seen));
        return false;
      }
    },
    [client, queryClient, workspaceId],
  );
}

function laterOf(current: string | null | undefined, next: string): string {
  return current && Date.parse(current) > Date.parse(next) ? current : next;
}

/**
 * The name a chat shows anywhere in the app: the member's own title if they
 * set one, else the fallback every reader of a chat title shares -- the
 * sidebar's list, the recents list, and the follow panel's header. `null`
 * covers the panel header's other case, a conversation that has not been
 * opened yet, with the exact same fallback rather than a second one next to
 * it: there is nothing left to reach for once a chat has neither a title nor
 * an identity yet.
 */
export function specConversationTitle(conversation: SpecConversationSummary | null): string {
  const title = conversation?.title?.trim();
  return title && title.length > 0 ? title : 'New chat';
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Coarse relative age for a recents list. Deliberately not a live clock: the
 * caller passes `now`, so a test pins it and a render never depends on when it
 * happened to run.
 */
export function specRelativeTime(iso: string | null, now: number): string {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const elapsed = Math.max(0, now - then);
  if (elapsed < MINUTE) return 'just now';
  if (elapsed < HOUR) {
    const minutes = Math.floor(elapsed / MINUTE);
    return `${minutes}m ago`;
  }
  if (elapsed < DAY) {
    const hours = Math.floor(elapsed / HOUR);
    return `${hours}h ago`;
  }
  const days = Math.floor(elapsed / DAY);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}
